import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import '../Education.css';

const CryptoEducation: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  };

  const curriculum = [
    {
      title: 'Cryptocurrency Fundamentals',
      lessons: [
        'What is cryptocurrency and blockchain technology',
        'How Bitcoin and Ethereum work',
        'Public vs. private keys and wallet security',
        'Cryptocurrency exchanges and how to use them',
        'Understanding market cap, circulating supply, and tokenomics',
        'The difference between coins and tokens'
      ]
    },
    {
      title: 'Major Cryptocurrencies',
      lessons: [
        'Bitcoin (BTC): Digital gold and store of value',
        'Ethereum (ETH): Smart contracts and DeFi',
        'Altcoins: XRP, Litecoin, Cardano, Solana, and more',
        'Stablecoins: USDT, USDC, and their role',
        'DeFi tokens and decentralized finance',
        'NFTs and their impact on crypto markets'
      ]
    },
    {
      title: 'Technical Analysis for Crypto',
      lessons: [
        'Reading crypto price charts and patterns',
        'Support and resistance in volatile markets',
        'Technical indicators for cryptocurrency',
        'Volume analysis and order book reading',
        'Identifying bull and bear markets in crypto',
        'Multi-timeframe analysis for crypto trading'
      ]
    },
    {
      title: 'Crypto Trading Strategies',
      lessons: [
        'Day trading cryptocurrencies',
        'Swing trading and holding positions',
        'HODLing: Long-term investment strategy',
        'Dollar-cost averaging (DCA)',
        'Arbitrage opportunities across exchanges',
        'Altcoin rotation and trend following'
      ]
    },
    {
      title: 'Security & Risk Management',
      lessons: [
        'Protecting your crypto with hardware wallets',
        'Two-factor authentication and security best practices',
        'Avoiding scams, rug pulls, and pump-and-dump schemes',
        'Position sizing in volatile crypto markets',
        'Managing extreme volatility and drawdowns',
        'Tax considerations for cryptocurrency trading'
      ]
    }
  ];

  return (
    <motion.div className="education-page" initial="hidden" animate="visible" variants={containerVariants}>
      <div className="page-content">
        <motion.div className="page-title" variants={itemVariants}>
          <h1>Cryptocurrency Trading Education</h1>
          <p>Understand cryptocurrency markets, blockchain technology, and digital assets</p>
        </motion.div>

        <motion.section className="course-overview" variants={itemVariants}>
          <h2>Why Learn Crypto Trading?</h2>
          <p>
            Cryptocurrency represents the future of finance - a decentralized, digital revolution in how we store and 
            transfer value. Learning to trade crypto opens opportunities in one of the fastest-growing and most innovative 
            markets, available 24/7/365.
          </p>
          <div className="course-stats">
            <div className="stat"><h3>24/7/365</h3><p>Always Open</p></div>
            <div className="stat"><h3>High Volatility</h3><p>Big Opportunities</p></div>
            <div className="stat"><h3>Decentralized</h3><p>No Central Control</p></div>
            <div className="stat"><h3>Innovation</h3><p>Cutting-Edge Tech</p></div>
          </div>
        </motion.section>

        <motion.section className="learning-path" variants={itemVariants}>
          <h2>Crypto Trading Curriculum</h2>
          <div className="modules-list">
            {curriculum.map((module, index) => (
              <motion.div key={index} className="module-card" variants={itemVariants} whileHover={{ scale: 1.02 }}>
                <div className="module-header">
                  <span className="module-number">Module {index + 1}</span>
                </div>
                <h3>{module.title}</h3>
                <ul className="topics-list">
                  {module.lessons.map((lesson, lessonIndex) => (
                    <li key={lessonIndex}>{lesson}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </motion.section>

        <motion.section className="market-factors" variants={itemVariants}>
          <h2>What Moves Crypto Prices?</h2>
          <div className="factors-grid">
            <div className="factor"><h4>📊 Supply & Demand</h4><p>Bitcoin's 21M cap and halving events</p></div>
            <div className="factor"><h4>📰 News & Sentiment</h4><p>Media coverage, influencer tweets, adoption news</p></div>
            <div className="factor"><h4>⚖️ Regulation</h4><p>Government policies and legal frameworks</p></div>
            <div className="factor"><h4>🔧 Technology</h4><p>Network upgrades, scalability solutions, security</p></div>
            <div className="factor"><h4>🏦 Institutional Adoption</h4><p>Corporate treasuries, ETFs, mainstream acceptance</p></div>
            <div className="factor"><h4>⛏️ Mining & Halving</h4><p>Bitcoin halving events reducing supply</p></div>
          </div>
        </motion.section>

        <motion.section className="risk-warning" variants={itemVariants}>
          <h2>⚠️ Critical Security & Risk Information</h2>
          <div className="warning-box">
            <p><strong>Extreme Volatility:</strong> Crypto can swing 10-20%+ in a single day</p>
            <p><strong>Regulatory Risk:</strong> Government actions can drastically impact prices</p>
            <p><strong>Security Risks:</strong> Exchange hacks, wallet security, and scams are real threats</p>
            <p><strong>Market Manipulation:</strong> Pump and dump schemes, whale manipulation</p>
            <p><strong>Never invest more than you can afford to lose!</strong></p>
            <p><strong>Use hardware wallets for long-term holdings</strong></p>
            <p><strong>Enable 2FA on all accounts</strong></p>
          </div>
        </motion.section>

        <motion.section className="trading-strategies" variants={itemVariants}>
          <h2>Crypto Trading Strategies</h2>
          <div className="strategies-grid">
            <div className="strategy"><h3>📈 HODLing</h3><p>Long-term buy-and-hold strategy</p></div>
            <div className="strategy"><h3>📊 Day Trading</h3><p>Multiple trades per day on volatility</p></div>
            <div className="strategy"><h3>🔄 Swing Trading</h3><p>Hold positions for days or weeks</p></div>
            <div className="strategy"><h3>⚡ Scalping</h3><p>Quick trades for small profits</p></div>
            <div className="strategy"><h3>💎 DCA Strategy</h3><p>Dollar-cost averaging to reduce volatility impact</p></div>
            <div className="strategy"><h3>🔄 Altcoin Rotation</h3><p>Rotate between Bitcoin and altcoins</p></div>
          </div>
        </motion.section>

        <motion.section className="success-tips" variants={itemVariants}>
          <h2>Cryptocurrency Trading Tips</h2>
          <ul className="tips-list">
            <li>Start with Bitcoin and Ethereum before exploring altcoins</li>
            <li>Use hardware wallets (Ledger, Trezor) for long-term holdings</li>
            <li>Never share your private keys or seed phrases with anyone</li>
            <li>Use 2FA on all exchange accounts</li>
            <li>Beware of "too good to be true" promises and fake giveaways</li>
            <li>Research projects thoroughly - read whitepapers</li>
            <li>Set strict stop-losses due to extreme volatility</li>
            <li>Keep emotions in check - FOMO and FUD lead to mistakes</li>
            <li>Stay updated on regulatory developments</li>
            <li>Practice with small amounts before committing large capital</li>
          </ul>
        </motion.section>

        <motion.div className="cta-section" variants={itemVariants}>
          <h2>Master Cryptocurrency Trading</h2>
          <p>Learn safe crypto trading with professional guidance and signals</p>
          <div className="btn-container">
            <Link to="/contact" className="btn">Enroll in Crypto Course</Link>
            <Link to="/services/signals" className="btn secondary">Get Crypto Signals</Link>
            <Link to="/markets/crypto" className="btn secondary">Explore Crypto Markets</Link>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default CryptoEducation;
